import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";
import { AuthContext } from "context/AuthContext";
import { getDeviceType } from "../utils/device";

// Server error messages are already written for the person reading them
// (and are deliberately vague where being specific would leak whether an
// account exists), so prefer them over anything invented here.
const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// ── Sign-in ────────────────────────────────────────────────────────────────

// Step one. Returns { twoFactorRequired } — true means the password was
// accepted but NO session exists yet: the browser now holds a short-lived
// challenge cookie and must post a code to verifyMfa below.
export const loginApi = async (data) =>
  api.post(API_ROUTES.LOGIN, data, {
    headers: { "x-device-type": getDeviceType() },
  });

// Step two, and also the last step of enrolment. Which one it is depends on
// which cookie the browser is holding; the server works it out.
export const verifyMfaApi = async (code) =>
  api.post(API_ROUTES.MFA_VERIFY, { code });

export const verifyBackupCodeApi = async (code) =>
  api.post(API_ROUTES.MFA_BACKUP_CODE, { code });

// ── Two-factor enrolment ───────────────────────────────────────────────────

// Arms 2FA and returns the QR (rendered server-side, so no QR library ships in
// this bundle), the secret for manual entry, and the backup codes. Two-factor
// is NOT active until a code is verified.
export const useStartMfaSetupMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, error } = useMutation({
    mutationFn: (password) => api.post(API_ROUTES.MFA_SETUP, { password }),
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not start setup")),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.ME] }),
  });
  return { mutate, mutateAsync, isPending, error };
};

export const useRegenerateBackupCodesMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: (password) =>
      api.post(API_ROUTES.MFA_REGENERATE_CODES, { password }),
    onSuccess: () =>
      showToast("success", "New backup codes generated. Save them now."),
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not regenerate codes")),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.ME] }),
  });
  return { mutate, mutateAsync, isPending };
};

// superAdmin only. Clears another admin's authenticator so they can enrol
// again — the way back from a lost phone.
export const useResetAdminMfaMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (id) => api.post(`${API_ROUTES.RESET_ADMIN_MFA}/${id}/reset-2fa`),
    onSuccess: () =>
      showToast("success", "Two-factor reset. They'll enrol again at next sign-in."),
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not reset two-factor")),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_ADMIN] }),
  });
  return { mutate, isPending };
};

// ── Sessions ───────────────────────────────────────────────────────────────

export const useSessionsQuery = () => {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [API_ROUTES.GET_SESSIONS],
    queryFn: async () => (await api.get(API_ROUTES.GET_SESSIONS)).data,
  });
  return { sessions: data?.sessions ?? [], isPending, isError, refetch };
};

export const useSignOutEverywhereMutation = () => {
  // clearSession, not logout: /logoutfromalldevices has already destroyed the
  // session server-side, so logout() would fire a redundant second request.
  const { clearSession } = useContext(AuthContext);
  const navigate = useNavigate();
  const { mutate, isPending } = useMutation({
    mutationFn: () => api.get(API_ROUTES.LOGOUT_ALL_DEVICES),
    onSuccess: () => {
      showToast("success", "Signed out on every device");
      clearSession();
      navigate("/login");
    },
    onError: (err) => showToast("error", errorMessage(err, "Could not sign out")),
  });
  return { mutate, isPending };
};

// ── Passwords ──────────────────────────────────────────────────────────────

export const useUpdatePasswordMutation = () => {
  // Changing the password revokes every session server-side, this one
  // included — so there is nothing left to sign out of.
  const { clearSession } = useContext(AuthContext);
  const navigate = useNavigate();
  const { mutate, isError, error, isSuccess, isPending } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.UPDATE_PASSWORD, data),
    onSuccess: () => {
      showToast("success", "Password updated successfully, please login again");
      clearSession();
      navigate("/login");
    },
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not update password")),
  });
  return { mutate, isError, error, isSuccess, isPending };
};

// Is the emailed link still good? Asked once when /password loads, so an
// expired link shows a message instead of a form that can only fail on submit.
//
// Always answers 200 with { valid, reason } — a rejected token is a normal
// outcome here, not a request error, so there is no error branch to handle.
// `retry: false` because a "no" is final; retrying cannot change it.
export const useCheckResetTokenQuery = (token) => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.CHECK_RESET_TOKEN, token],
    queryFn: async () =>
      (await api.get(`${API_ROUTES.CHECK_RESET_TOKEN}/${token}`)).data,
    enabled: !!token,
    retry: false,
    // The answer cannot change while the page is open, and refetching on tab
    // focus would spend the endpoint's rate limit for nothing.
    refetchOnWindowFocus: false,
  });

  return {
    // Treat "still loading" and "request failed" as valid: the form renders and
    // setPassword gives the authoritative answer. The opposite default would
    // tell an admin holding a good link that it had expired.
    isValid: isPending ? true : data?.data?.valid !== false,
    reason: data?.data?.reason ?? null,
    isPending,
  };
};

export const useSetPasswordMutation = () => {
  const navigate = useNavigate();
  const { mutate, isError, error, isSuccess, isPending } = useMutation({
    mutationFn: (data) =>
      api.post(`${API_ROUTES.SET_PASSWORD}/${data?.token}`, data),
    onSuccess: () => {
      showToast("success", "Password set successfully");
      navigate("/login");
    },
    onError: (err) =>
      showToast(
        "error",
        errorMessage(err, "This link is invalid or has expired"),
      ),
  });
  return { mutate, isError, error, isSuccess, isPending };
};

// Re-sends an ACTIVATION link, and only for an account still awaiting one.
export const useSendEmailMutation = () => {
  const { mutate, isError, error, isSuccess, isPending } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.SEND_EMAIL, data),
    onSuccess: (res) =>
      showToast("success", res?.data?.message || "Email sent successfully"),
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not send email")),
  });
  return { mutate, isError, error, isSuccess, isPending };
};

// Forgot password, for an admin who already has one.
export const useForgotPasswordMutation = () => {
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.FORGOT_PASSWORD, data),
    onSuccess: (res) =>
      showToast(
        "success",
        res?.data?.message ||
          "If that account exists, a reset email has been sent.",
      ),
    onError: (err) =>
      showToast("error", errorMessage(err, "Could not send reset email")),
  });
  return { mutate, isPending, isSuccess };
};
